import Cookies from 'js-cookie';
import { domain } from '../d-man';
import { ObjectList } from './ObjectList';

export type UserAccessPermission = {
    code: string;
    /** Human-readable, e.g. "Allows a user to approve an order". */
    description: string;
    /** Delivery, Order, Requisition or Project. */
    category: string;
    /** The user's roles conferring this, each with the scope it applies in. */
    grantedBy: string[];
};

export type UserAccessRole = {
    roleName: string;
    scopeType: string;
    scopeId?: string;
    scopeName: string;
    permissions: string[];
};

export type UserAccessRow = {
    userId: string;
    userName: string;
    firstName?: string;
    lastName?: string;
    email: string;
    emailConfirmed: boolean;
    twoFactorEnabled: boolean;
    lockoutEnabled: boolean;
    lockoutEnd?: string;
    isCurrentlyLockedOut: boolean;
    accessFailedCount: number;
    roles: UserAccessRole[];
    effectivePermissions: UserAccessPermission[];
    roleCount: number;
    permissionCount: number;
    isAdmin: boolean;
    hasNoRoles: boolean;
};

/**
 * Current-state access report for every user. The endpoint is restricted to
 * level6 server-side, so a non-admin gets a 403 rather than an empty list.
 */
export function useGetUserAccessReport() {
    return domain.useGet<ObjectList<UserAccessRow>>('/audit/useraccess');
}

/**
 * Downloads the PDF edition of the report.
 *
 * Fetched directly rather than through d-man: the response is a binary file,
 * not JSON, and it is generated server-side so the document is authoritative
 * rather than assembled from whatever the browser happens to be holding.
 */
export async function downloadUserAccessPdf() {
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const response = await fetch(`${baseUrl}audit/useraccess/pdf`, {
        headers: { Authorization: `Bearer ${Cookies.get('access_token')}` },
    });

    if (!response.ok) {
        throw new Error(`Could not generate the PDF (${response.status}).`);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bic-user-access-audit-${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
